import type { PopulationBlob } from './blobLoader';
import {
  ECONOMIC_ACTIVITY_LABEL,
  SEX_LABEL,
} from './labels';

export type AttributeKind = 'categorical' | 'ordinal' | 'boolean';

export type AttributeValue = { value: number; label: string };

export type AttributeAccessor = (blob: PopulationBlob, i: number) => number;

export type AttributeValuesProvider = (blob: PopulationBlob) => AttributeValue[];

export type Attribute = {
  key: string;
  label: string;
  kind: AttributeKind;
  accessor: AttributeAccessor;
  values: AttributeValuesProvider;
};

const ageBandValues: AttributeValuesProvider = (blob) =>
  blob.ageBands.map((b) => ({ value: b.id, label: b.label }));

const sexValues: AttributeValuesProvider = () => [
  { value: 0, label: SEX_LABEL.m },
  { value: 1, label: SEX_LABEL.f },
];

const householdIncomeBandValues: AttributeValuesProvider = (blob) =>
  blob.incomeBands.map((b) => ({ value: b.id, label: b.label }));

function occupationLabel(code: string): string {
  if (code === 'not_applicable') return 'Not applicable';
  if (code === 'student') return 'Student';
  return `NS-SeC ${code}`;
}

const occupationValues: AttributeValuesProvider = (blob) =>
  blob.occupations.map((code, value) => ({ value, label: occupationLabel(code) }));

const economicActivityValues: AttributeValuesProvider = (blob) =>
  blob.economicActivities.map((code, value) => ({
    value,
    label: ECONOMIC_ACTIVITY_LABEL[code] ?? code,
  }));

const regionValues: AttributeValuesProvider = (blob) =>
  blob.regions.map((code, value) => ({ value, label: code }));

export const ATTRIBUTES: Attribute[] = [
  {
    key: 'age_band',
    label: 'Age band',
    kind: 'ordinal',
    accessor: (blob, i) => blob.data.ageBands[i],
    values: ageBandValues,
  },
  {
    key: 'sex',
    label: 'Sex',
    kind: 'categorical',
    accessor: (blob, i) => blob.data.sex[i],
    values: sexValues,
  },
  {
    key: 'household_income_band',
    label: 'Household income band',
    kind: 'ordinal',
    accessor: (blob, i) => blob.data.householdIncomeBand[i],
    values: householdIncomeBandValues,
  },
  {
    key: 'occupation',
    label: 'Occupation (NS-SeC)',
    kind: 'categorical',
    accessor: (blob, i) => blob.data.occupationIndices[i],
    values: occupationValues,
  },
  {
    key: 'economic_activity',
    label: 'Economic activity',
    kind: 'categorical',
    accessor: (blob, i) => blob.data.economicActivity[i],
    values: economicActivityValues,
  },
  {
    key: 'region',
    label: 'Region (LSOA)',
    kind: 'categorical',
    accessor: (blob, i) => blob.data.regionIndices[i],
    values: regionValues,
  },
];

export const ATTRIBUTES_BY_KEY: Record<string, Attribute> = Object.fromEntries(
  ATTRIBUTES.map((a) => [a.key, a]),
);

export function getAttribute(key: string): Attribute | undefined {
  return ATTRIBUTES_BY_KEY[key];
}

export function assertAttributesAtIndexZero(blob: PopulationBlob): void {
  if (blob.data.length === 0) return;
  for (const attr of ATTRIBUTES) {
    const v = attr.accessor(blob, 0);
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) {
      throw new Error(
        `[attributes] ${attr.key}: accessor returned invalid value ${String(v)} at index 0`,
      );
    }
    const allowed = attr.values(blob);
    const ok = allowed.some((entry) => entry.value === v);
    if (!ok) {
      throw new Error(
        `[attributes] ${attr.key}: value ${v} at index 0 is not in the registry's value set ` +
          `(size=${allowed.length})`,
      );
    }
  }
  console.info(`[attributes] index-0 accessor check passed for ${ATTRIBUTES.length} attributes`);
}
